import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';
import { fetchSnapshotProposals } from '../data/snapshot';
import VoteModal from './VoteModal';

const ProposalList = () => {
  const { isConnected, address, connectWallet } = useWallet();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState('aave.eth');

  useEffect(() => {
    const loadProposals = async () => {
      try {
        setLoading(true);
        const proposalData = await fetchSnapshotProposals(selectedSpace, 8);
        setProposals(proposalData);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProposals();
  }, [selectedSpace]);

  const getStatus = (proposal) => {
    const now = Date.now() / 1000;
    if (proposal.state === 'closed') return 'closed';
    if (now < proposal.start) return 'pending';
    if (now > proposal.end) return 'closed';
    return 'active';
  };

  const getStatusClass = (status) => {
    const base = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'active':
        return `${base} bg-green-100 text-green-800`;
      case 'pending':
        return `${base} bg-orange-100 text-orange-800`;
      case 'closed':
        return `${base} bg-gray-100 text-gray-600`;
      default:
        return `${base} bg-gray-100 text-gray-600`;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const formatTimeRemaining = (endTimestamp) => {
    const now = Date.now() / 1000;
    const diff = endTimestamp - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (24 * 60 * 60));
    const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h left`;
    return 'Ending soon';
  };

  const handleVoteClick = (proposal) => {
    if (!isConnected) {
      // Trigger wallet connection
      connectWallet('metaMask');
      return;
    }
    setSelectedProposal(proposal);
    setShowVoteModal(true);
  };

  const getVotingPower = (proposal) => {
    // This would be replaced with actual voting power calculation
    // For now, return a mock value or fetch from Snapshot
    return '1.0';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-dominant">DAO Proposals</h2>
          <div className="animate-pulse bg-gray-200 h-6 w-24 rounded"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-4">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="text-center text-red-600">
          <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
          <p>Failed to load proposals: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-dominant transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-dominant">DAO Proposals</h2>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <select 
              value={selectedSpace}
              onChange={(e) => setSelectedSpace(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary"
            >
              <option value="aave.eth">Aave</option>
              <option value="uniswap">Uniswap</option>
              <option value="compound">Compound</option>
              <option value="makerdao.eth">MakerDAO</option>
              <option value="ens.eth">ENS</option>
            </select>
            
            <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              {proposals.length} proposals
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {proposals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <i className="fas fa-inbox text-3xl mb-2"></i>
              <p>No proposals found for this DAO</p>
            </div>
          ) : (
            proposals.map((proposal) => {
              const status = getStatus(proposal);
              const timeRemaining = formatTimeRemaining(proposal.end);
              const votingPower = getVotingPower(proposal);
              
              return (
                <div key={proposal.id} className="border border-gray-200 rounded-lg p-4 hover:border-secondary transition-colors">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 flex-1">
                          {proposal.title}
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className={getStatusClass(status)}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                          {isConnected && status === 'active' && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              {votingPower} VP
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {proposal.body?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="fas fa-calendar"></i>
                          Start: {formatTime(proposal.start)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="fas fa-clock"></i>
                          End: {formatTime(proposal.end)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="fas fa-users"></i>
                          Votes: {proposal.votes || 0}
                        </span>
                        {status === 'active' && (
                          <span className="flex items-center gap-1 text-orange-600">
                            <i className="fas fa-hourglass-half"></i>
                            {timeRemaining}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 min-w-[120px]">
                      <button 
                        onClick={() => handleVoteClick(proposal)}
                        disabled={!isConnected || status !== 'active'}
                        className="bg-secondary text-white px-4 py-2 rounded-lg hover:bg-dominant disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        {!isConnected ? (
                          <>
                            <i className="fas fa-wallet"></i>
                            Connect to Vote
                          </>
                        ) : status !== 'active' ? (
                          <>
                            <i className="fas fa-lock"></i>
                            Voting Closed
                          </>
                        ) : (
                          <>
                            <i className="fas fa-vote-yea"></i>
                            Vote
                          </>
                        )}
                      </button>
                      
                      <a
                        href={`https://snapshot.org/#/${proposal.space.id}/proposal/${proposal.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-center text-secondary hover:text-dominant text-sm transition-colors flex items-center justify-center gap-1"
                      >
                        View Details
                        <i className="fas fa-external-link-alt text-xs"></i>
                      </a>
                    </div>
                  </div>

                  {/* Choices Preview */}
                  {proposal.choices && proposal.choices.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">Choices:</div>
                      <div className="flex flex-wrap gap-2">
                        {proposal.choices.slice(0, 3).map((choice, index) => (
                          <span 
                            key={index}
                            className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                          >
                            {choice}
                          </span>
                        ))}
                        {proposal.choices.length > 3 && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            +{proposal.choices.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Scores if available */}
                  {proposal.scores && proposal.scores.length > 0 && status === 'closed' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">Results:</div>
                      <div className="space-y-1">
                        {proposal.choices.map((choice, index) => (
                          <div key={index} className="flex justify-between items-center text-xs">
                            <span className="truncate flex-1 mr-2">{choice}</span>
                            <span className="font-medium text-gray-700">
                              {proposal.scores[index] || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Load More Button */}
        {proposals.length > 0 && (
          <div className="mt-6 text-center">
            <button className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors">
              Load More Proposals
            </button>
          </div>
        )}
      </div>

      <VoteModal 
        proposal={selectedProposal}
        isOpen={showVoteModal}
        onClose={() => {
          setShowVoteModal(false);
          setSelectedProposal(null);
        }}
      />
    </>
  );
};

export default ProposalList;